// Inline-SVG trend chart for the financial dashboards.
//
// No chart library: monthly sales/expense bars plus a net-profit line. The
// viewBox scales to the available width, and every point carries a <title>
// so a hover shows the exact amount.

import { css, html, nothing } from 'lit';
import type { TemplateResult } from 'lit';
import type { DashboardMonth } from '../lib/api.js';
import { formatCompactMoney, formatMoney } from '../lib/finance.js';

export const FINANCE_CHART_STYLES = css`
  .finance-chart { width: 100%; height: auto; display: block; overflow: visible; }
  .finance-chart .grid { stroke: var(--color-border, #e5e7eb); stroke-width: 1; }
  .finance-chart .axis { fill: var(--color-text-secondary, #5f6368); font-size: 10px; }
  .finance-chart .bar-sales { fill: var(--color-primary, #1a73e8); }
  .finance-chart .bar-expenses { fill: var(--color-warning, #e8a317); opacity: 0.85; }
  .finance-chart .profit-line { fill: none; stroke: var(--color-success, #188038); stroke-width: 2.5; }
  .finance-chart .profit-dot { fill: var(--color-success, #188038); }
  .finance-chart .profit-dot.negative { fill: var(--color-error, #d93025); }
  .finance-chart .zero { stroke: var(--color-text-secondary, #5f6368); stroke-width: 1; opacity: 0.5; }
  .chart-legend { display: flex; flex-wrap: wrap; gap: 0.9rem; font-size: 0.72rem; color: var(--color-text-secondary); margin-top: 0.35rem; }
  .chart-legend span { display: inline-flex; align-items: center; gap: 0.35rem; }
  .chart-legend i { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }
  .chart-legend i.sales { background: var(--color-primary, #1a73e8); }
  .chart-legend i.expenses { background: var(--color-warning, #e8a317); }
  .chart-legend i.profit { background: var(--color-success, #188038); border-radius: 999px; }
`;

function shortMonth(key: string): string {
  const [year, month] = key.split('-').map(Number);
  if (!year || !month) return key;
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'short' });
}

function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

export function renderFinanceChart(months: DashboardMonth[]): TemplateResult | typeof nothing {
  const rows = months.slice(-6);
  if (!rows.length) return nothing;

  const width = 660;
  const height = 250;
  const left = 62;
  const right = 14;
  const top = 16;
  const bottom = 32;
  const plotW = width - left - right;
  const plotH = height - top - bottom;

  const values = rows.flatMap((r) => [Number(r.sales) || 0, Number(r.expenses) || 0, Number(r.netProfit) || 0]);
  const rawMax = Math.max(0, ...values);
  const rawMin = Math.min(0, ...values);
  const span = Math.max(rawMax - rawMin, 1);
  const yMax = rawMax + span * 0.08;
  const yMin = rawMin < 0 ? rawMin - span * 0.08 : 0;

  const y = (value: number) => top + ((yMax - value) / (yMax - yMin)) * plotH;
  const zeroY = y(0);

  const slot = plotW / rows.length;
  const barW = Math.max(8, Math.min(30, slot * 0.28));
  const gap = Math.max(3, barW * 0.18);

  const ticks = [yMax, yMax / 2, 0, ...(yMin < 0 ? [yMin / 2, yMin] : [])];
  const uniqueTicks = [...new Set(ticks.map((t) => Math.round(t * 100) / 100))];

  const points = rows.map((row, i) => {
    const cx = left + slot * i + slot / 2;
    return {
      x: cx,
      y: y(Number(row.netProfit) || 0),
      value: Number(row.netProfit) || 0,
      month: row.month,
    };
  });

  return html`
    <svg class="finance-chart" viewBox="0 0 ${width} ${height}" role="img"
      aria-label="Monthly sales, expenses and net profit">
      ${uniqueTicks.map((tick) => html`
        <line class="grid" x1=${left} x2=${width - right} y1=${y(tick)} y2=${y(tick)}></line>
        <text class="axis" x=${left - 8} y=${y(tick) + 3} text-anchor="end">${formatCompactMoney(tick)}</text>
      `)}
      <line class="zero" x1=${left} x2=${width - right} y1=${zeroY} y2=${zeroY}></line>
      ${rows.map((row, i) => {
        const cx = left + slot * i + slot / 2;
        const sales = Math.max(0, Number(row.sales) || 0);
        const expenses = Math.max(0, Number(row.expenses) || 0);
        const salesY = y(sales);
        const expensesY = y(expenses);
        return html`
          <rect class="bar-sales" x=${cx - barW - gap / 2} y=${salesY}
            width=${barW} height=${Math.max(0, zeroY - salesY)} rx="2">
            <title>${shortMonth(row.month)} sales: ${formatMoney(sales)}</title>
          </rect>
          <rect class="bar-expenses" x=${cx + gap / 2} y=${expensesY}
            width=${barW} height=${Math.max(0, zeroY - expensesY)} rx="2">
            <title>${shortMonth(row.month)} expenses: ${formatMoney(expenses)}</title>
          </rect>
          <text class="axis" x=${cx} y=${height - 10} text-anchor="middle">${shortMonth(row.month)}</text>
        `;
      })}
      <polyline class="profit-line" points=${points.map((p) => `${p.x},${p.y}`).join(' ')}></polyline>
      ${points.map((p) => html`
        <circle class="profit-dot ${p.value < 0 ? 'negative' : ''}" cx=${p.x} cy=${p.y} r="3.5">
          <title>${shortMonth(p.month)} net profit: ${formatMoney(p.value)}</title>
        </circle>
      `)}
    </svg>
    <div class="chart-legend">
      <span><i class="sales"></i>Sales (net)</span>
      <span><i class="expenses"></i>Expenses</span>
      <span><i class="profit"></i>Net profit</span>
    </div>
  `;
}
